import type Phaser from 'phaser';
import type { AudioManifest } from './audio/manager';
export interface SheetDef { file: string; frameWidth: number; frameHeight: number; frames: number; fps: number; anchorX: number; anchorY: number; scale: number; }
export interface ClipDef { frames: number; fps: number; }
export interface AtlasDef { dir: string; displayScale: number; directions: string[]; animMap: Record<string, string>; clips: Record<string, ClipDef>; }
/** `tilesets` : atlas de tuiles Retro par sous-zone (chemin sans extension, .png + .json). */
/** `audio` : effets et musiques (tools/build-audio.py), chargés après le démarrage par src/client/audio. */
export interface Manifest { sheets: Record<string, SheetDef>; images: Record<string, string>; atlases?: Record<string, AtlasDef>; tilesets?: Record<string, string>; audio?: AudioManifest; }
export const atlasKey = (sprite: string, clip: string): string => `atlas:${sprite}:${clip}`;
export const tilesetKey = (biome: string): string => `tiles:${biome}`;
export async function loadManifest(): Promise<Manifest> { const r = await fetch('/assets/manifest.json'); if (!r.ok) throw new Error('manifest introuvable'); return r.json() as Promise<Manifest>; }
/** Actifs d'une salle (cf. `Run.roomAssets`) : image de fond, sous-zone et sprites présents. */
export interface RoomAssets { map: string; biome?: string; sprites: string[]; }
/** Sous-ensemble du chargeur et du gestionnaire de textures utilisé ici (testable sans Phaser). */
interface LoaderLike { image(key: string, url: string): unknown; atlas(key: string, png: string, json: string): unknown; spritesheet(key: string, url: string, cfg: { frameWidth: number; frameHeight: number; endFrame: number }): unknown; }
interface TexturesLike { exists(key: string): boolean; }

/** Démarrage : icônes et images d'interface seulement (les cartes `maps/*`, les atlas de tuiles et d'entités sont chargés à l'entrée de chaque salle). */
export function queueBootAssets(loader: LoaderLike, m: Manifest): void {
  for (const [key, s] of Object.entries(m.sheets)) loader.spritesheet(key, `/assets/${s.file}`, { frameWidth: s.frameWidth, frameHeight: s.frameHeight, endFrame: s.frames - 1 });
  for (const [key, file] of Object.entries(m.images)) if (!key.startsWith('maps/')) loader.image(key, `/assets/${file}`);
}

/** Entrée (ou préchargement) d'une salle : met en file ce qui manque encore ; renvoie les clés mises en file. */
export function queueRoomAssets(loader: LoaderLike, m: Manifest, textures: TexturesLike, a: RoomAssets): string[] {
  const queued: string[] = [];
  const image = m.images[a.map];
  if (image && !textures.exists(a.map)) { loader.image(a.map, `/assets/${image}`); queued.push(a.map); }
  const tiles = a.biome ? m.tilesets?.[a.biome] : undefined;
  if (a.biome && tiles && !textures.exists(tilesetKey(a.biome))) { loader.atlas(tilesetKey(a.biome), `/assets/${tiles}.png`, `/assets/${tiles}.json`); queued.push(tilesetKey(a.biome)); }
  for (const sprite of a.sprites) {
    const def = m.atlases?.[sprite];
    if (!def) continue;
    for (const clip of Object.keys(def.clips)) {
      const key = atlasKey(sprite, clip);
      if (textures.exists(key)) continue;
      loader.atlas(key, `/assets/${def.dir}/${clip}.png`, `/assets/${def.dir}/${clip}.json`);
      queued.push(key);
    }
  }
  return queued;
}
/** Tout le manifest d'un coup (outils et tests) ; le jeu charge par salle avec `queueBootAssets` + `queueRoomAssets`. */
export function queueAssets(loader: Phaser.Loader.LoaderPlugin, m: Manifest): void {
  queueBootAssets(loader, m);
  for (const [key, file] of Object.entries(m.images)) if (key.startsWith('maps/')) loader.image(key, `/assets/${file}`);
  for (const [biome, path] of Object.entries(m.tilesets ?? {})) loader.atlas(tilesetKey(biome), `/assets/${path}.png`, `/assets/${path}.json`);
  for (const [sprite, a] of Object.entries(m.atlases ?? {})) {
    for (const clip of Object.keys(a.clips)) loader.atlas(atlasKey(sprite, clip), `/assets/${a.dir}/${clip}.png`, `/assets/${a.dir}/${clip}.json`);
  }
}
/** Enregistre les animations des planches et des atlas déjà chargés (`textures`) ; rappelée à chaque salle pour les atlas arrivés depuis. */
export function registerAnimations(anims: Phaser.Animations.AnimationManager, m: Manifest, textures: TexturesLike): void {
  for (const [key, s] of Object.entries(m.sheets)) {
    if (anims.exists(key)) continue;
    const loop = key.endsWith('-idle') || key.endsWith('-run');
    anims.create({ key, frames: anims.generateFrameNumbers(key, { start: 0, end: s.frames - 1 }), frameRate: s.fps, repeat: loop ? -1 : 0 });
  }
  for (const [sprite, a] of Object.entries(m.atlases ?? {})) {
    for (const [clip, c] of Object.entries(a.clips)) {
      const key = atlasKey(sprite, clip);
      if (anims.exists(key) || !textures.exists(key)) continue; // atlas pas encore chargé : à la prochaine salle
      // frames de l'atlas : `<clip>/0000`, `<clip>/0001`, ...
      anims.create({ key, frames: anims.generateFrameNames(key, { prefix: `${clip}/`, start: 0, end: c.frames - 1, zeroPad: 4 }), frameRate: c.fps, repeat: /^(static|walk|run)/.test(clip) ? -1 : 0 });
    }
  }
}
